<template>
  <div class="page inventory-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">Estoque</h1>
        <p class="page-subtitle">Insumos, bandejas abertas e movimentações</p>
      </div>
    </div>

    <div class="inventory-tabs" role="tablist" aria-label="Lista de insumos">
      <button
        type="button"
        class="inventory-tab"
        :class="{ 'inventory-tab--active': listTab === 'active' }"
        role="tab"
        :aria-selected="listTab === 'active'"
        @click="listTab = 'active'"
      >
        Ativos
      </button>
      <button
        type="button"
        class="inventory-tab"
        :class="{ 'inventory-tab--active': listTab === 'archived' }"
        role="tab"
        :aria-selected="listTab === 'archived'"
        @click="listTab = 'archived'"
      >
        Arquivados
        <span v-if="archivedSupplies.length" class="inventory-tab-count">{{ archivedSupplies.length }}</span>
      </button>
      <button
        type="button"
        class="inventory-tab"
        :class="{ 'inventory-tab--active': listTab === 'movements' }"
        role="tab"
        :aria-selected="listTab === 'movements'"
        @click="listTab = 'movements'"
      >
        Movimentações
      </button>
    </div>

    <template v-if="listTab === 'movements'">
      <InventoryLedgerPanel show-supply-column />
    </template>

    <template v-else>
    <div class="inventory-filters">
      <div class="inventory-search-wrap">
        <label for="inventory-search" class="inventory-search-label">Filtrar</label>
        <input
          id="inventory-search"
          v-model="nameFilter"
          type="search"
          class="inventory-search-input"
          placeholder="Nome do insumo"
          aria-label="Filtrar por nome"
          autocomplete="off"
        />
        <i class="material-icons inventory-search-icon" aria-hidden="true">search</i>
      </div>
      <label v-if="listTab === 'active'" class="inventory-filter-checkbox checkbox-label">
        <input v-model="lowStockOnly" type="checkbox" />
        Somente estoque baixo
      </label>
    </div>

    <div class="card-list">
      <article
        v-for="supply in displayedSupplies"
        :key="supply.id"
        class="card card--with-icon"
        :class="{
          'card--low-stock': listTab === 'active' && isLowStock(supply),
          'card--archived': listTab === 'archived',
        }"
      >
        <div class="card-icon-wrap inventory-card-icon">
          <i class="material-icons">{{ supply.tracking === 'container' ? 'inventory_2' : 'category' }}</i>
        </div>
        <div class="card-content">
          <h3 class="card-title card-primary">{{ supply.name }}</h3>
          <p v-if="supply.category" class="card-meta card-secondary">{{ supply.category }}</p>
          <p class="card-meta card-accent">
            <template v-if="supply.tracking === 'quantity'">
              {{ formatQuantityStock(supply) }}
            </template>
            <template v-else>
              {{ sealedCountLabel(supply, supply.sealedCount) }}
              · {{ openCountLabel(supply, openCount(supply.id)) }}
            </template>
          </p>
          <p v-if="listTab === 'active' && isLowStock(supply)" class="low-stock-badge">
            <i class="material-icons" aria-hidden="true">warning_amber</i>
            Estoque baixo
          </p>
          <p v-if="listTab === 'archived'" class="archived-badge">Arquivado</p>
        </div>
        <div v-if="listTab === 'active'" class="card-actions">
          <button type="button" class="btn-icon" aria-label="Editar" @click="openEdit(supply)">
            <i class="material-icons">edit</i>
          </button>
          <button type="button" class="btn-icon" aria-label="Movimentar" @click="openMovement(supply)">
            <i class="material-icons">sync_alt</i>
          </button>
          <button type="button" class="btn-icon" aria-label="Histórico" @click="openSupplyHistory(supply)">
            <i class="material-icons">history</i>
          </button>
          <button
            type="button"
            class="btn-icon btn-icon-danger"
            aria-label="Excluir"
            :disabled="deleteCheckLoading && deleteTargetId === supply.id"
            @click="handleDeleteClick(supply)"
          >
            <i class="material-icons">delete_outline</i>
          </button>
        </div>
        <div v-else class="card-actions">
          <button type="button" class="btn-icon" aria-label="Histórico" @click="openSupplyHistory(supply)">
            <i class="material-icons">history</i>
          </button>
          <button
            type="button"
            class="btn-icon"
            aria-label="Reativar"
            :disabled="restoreLoadingId === supply.id"
            @click="restoreSupply(supply)"
          >
            <i class="material-icons">unarchive</i>
          </button>
        </div>
      </article>
    </div>

    <p v-if="listTab === 'active' && activeSupplies.length === 0" class="empty-hint">
      Nenhum insumo cadastrado. Adicione o primeiro.
    </p>
    <p v-else-if="listTab === 'archived' && archivedSupplies.length === 0" class="empty-hint">
      Nenhum insumo arquivado.
    </p>
    <p v-else-if="displayedSupplies.length === 0" class="empty-hint">
      Nenhum insumo encontrado com os filtros atuais.
    </p>
    </template>

    <button
      v-if="listTab === 'active'"
      type="button"
      class="btn-fab"
      aria-label="Novo insumo"
      @click="openAdd"
    >
      <i class="material-icons">add</i>
    </button>

    <!-- Supply form -->
    <Drawer :model-value="showModal" @update:model-value="showModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ editingId ? 'Alterar insumo' : 'Novo insumo' }}</h2>
        <button type="button" class="modal-close" aria-label="Fechar" @click="showModal = false">
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="save">
          <div class="form-group">
            <label for="supply-name">Nome</label>
            <input id="supply-name" v-model="formName" type="text" placeholder="Ex: 13D Volume" />
            <p v-if="nameError" class="field-error">{{ nameError }}</p>
          </div>
          <div class="form-group">
            <label for="supply-tracking">Tipo de controle</label>
            <v-select
              id="supply-tracking"
              :options="trackingOptions"
              label="label"
              :reduce="(option) => option.value"
              v-model="formTracking"
              placeholder="Selecione o tipo"
              :disabled="!!editingId"
              :clearable="false"
              :class="{ 'field-invalid': trackingError }"
            />
            <p v-if="trackingError" class="field-error">{{ trackingError }}</p>
          </div>
          <div class="form-group">
            <label for="supply-unit">Unidade (singular)</label>
            <input
              id="supply-unit"
              v-model="formUnit"
              type="text"
              placeholder="bandeja, frasco, un"
              :aria-invalid="!!unitError"
            />
            <p v-if="unitError" class="field-error">{{ unitError }}</p>
          </div>
          <div class="form-group">
            <label for="supply-unit-plural">Unidade (plural)</label>
            <input id="supply-unit-plural" v-model="formUnitPlural" type="text" placeholder="bandejas, frascos" />
          </div>
          <div class="form-group">
            <label for="supply-category">Categoria (opcional)</label>
            <input id="supply-category" v-model="formCategory" type="text" placeholder="lash, adhesive" />
          </div>
          <div class="form-group">
            <label for="supply-reorder">Alerta de estoque baixo</label>
            <input id="supply-reorder" v-model.number="formReorderLevel" type="number" min="0" step="1" />
          </div>
          <template v-if="!editingId">
            <div v-if="formTracking === 'quantity'" class="form-group">
              <label for="supply-qty">Quantidade inicial</label>
              <input id="supply-qty" v-model.number="formInitialQty" type="number" min="0" step="1" />
            </div>
            <template v-else>
              <div class="form-group">
                <label for="supply-sealed">{{ initialSealedLabel }}</label>
                <input
                  id="supply-sealed"
                  v-model.number="formInitialSealed"
                  type="number"
                  min="0"
                  step="1"
                  :aria-invalid="!!initialStockError"
                />
              </div>
              <div class="form-group">
                <label for="supply-open">{{ initialOpenLabel }}</label>
                <input
                  id="supply-open"
                  v-model.number="formInitialOpen"
                  type="number"
                  min="0"
                  step="1"
                  :aria-invalid="!!initialStockError"
                />
                <p v-if="initialStockError" class="field-error">{{ initialStockError }}</p>
              </div>
            </template>
          </template>
          <div class="modal-footer modal-footer--inline">
            <button type="button" class="btn btn-secondary" @click="showModal = false">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="saveLoading">
              {{ saveLoading ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </form>
      </div>
    </Drawer>

    <!-- Movement drawer -->
    <Drawer :model-value="showMovement" @update:model-value="showMovement = $event">
      <div class="modal-header">
        <h2 class="modal-title">Movimentar — {{ movementSupply?.name }}</h2>
        <button type="button" class="modal-close" aria-label="Fechar" @click="showMovement = false">
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label for="movement-type">Tipo</label>
          <v-select
            id="movement-type"
            :options="movementTypeOptions"
            label="label"
            :reduce="(option) => option.value"
            v-model="movementType"
            placeholder="Selecione o tipo"
            :clearable="false"
            :class="{ 'field-invalid': movementTypeError }"
          />
          <p v-if="movementTypeError" class="field-error">{{ movementTypeError }}</p>
        </div>
        <div v-if="movementType === 'manual_adjust'" class="form-group">
          <label for="movement-direction">Direção</label>
          <v-select
            id="movement-direction"
            :options="movementDirectionOptions"
            label="label"
            :reduce="(option) => option.value"
            v-model="movementDirection"
            placeholder="Selecione a direção"
            :clearable="false"
          />
        </div>
        <div class="form-group">
          <label for="movement-amount">{{ movementAmountLabel }}</label>
          <input
            id="movement-amount"
            v-model.number="movementAmount"
            type="number"
            min="1"
            step="1"
            :aria-invalid="!!movementAmountError"
          />
          <p v-if="movementAmountError" class="field-error">{{ movementAmountError }}</p>
          <p v-if="movementHint" class="field-hint">{{ movementHint }}</p>
        </div>
        <div class="form-group">
          <label for="movement-note">{{ movementNoteLabel }}</label>
          <textarea id="movement-note" v-model="movementNote" rows="2" :placeholder="movementNotePlaceholder" />
          <p v-if="movementNoteError" class="field-error">{{ movementNoteError }}</p>
        </div>
        <p v-if="movementActionError" class="field-error movement-action-error" role="alert">
          {{ movementActionError }}
        </p>
        <div class="modal-footer modal-footer--inline">
          <button type="button" class="btn btn-secondary" @click="showMovement = false">Cancelar</button>
          <button type="button" class="btn btn-primary" :disabled="movementLoading" @click="saveMovement">
            {{ movementLoading ? 'Salvando...' : 'Confirmar' }}
          </button>
        </div>
      </div>
    </Drawer>

    <Drawer :model-value="showHistory" @update:model-value="showHistory = $event">
      <div class="modal-header">
        <h2 class="modal-title">Histórico — {{ historySupply?.name }}</h2>
        <button type="button" class="modal-close" aria-label="Fechar" @click="showHistory = false">
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <InventoryLedgerPanel
          v-if="showHistory && historySupply"
          :key="historySupply.id"
          :fixed-supply-id="historySupply.id"
        />
      </div>
    </Drawer>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      :title="deleteDialogTitle"
      :message="deleteMessage"
      :confirm-label="deleteDialogConfirmLabel"
      :confirm-loading="deleteLoading"
      :confirm-loading-label="deleteDialogLoadingLabel"
      @confirm="doDelete"
    />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '@/firebase';
import { useSupplyStore } from '@/stores/supply';
import { useServiceStore } from '@/stores/service';
import { useUserStore } from '@/stores/user';
import { useToast } from '@/composables/useToast';
import { openActionLabel } from '@/utils/supplyLabels';
import {
  assessSupplyDeletion,
  buildServiceUpdatesUnlinkingSupply,
  formatArchiveDeleteMessage,
  formatBlockOpenContainersMessage,
  formatHardDeleteMessage,
} from '@/utils/supplyReferences';
import Drawer from '@/components/Drawer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import InventoryLedgerPanel from '@/components/InventoryLedgerPanel.vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { openCountLabel, sealedCountLabel } from '@/utils/supplyLabels';

const TRACKING_OPTIONS = [
  { value: 'quantity', label: 'Quantidade (un, par, etc.)' },
  { value: 'container', label: 'Aberto / fechado (bandeja, frasco)' },
];

export default {
  name: 'Inventory',
  components: { Drawer, ConfirmDialog, InventoryLedgerPanel, vSelect },
  setup() {
    const supplyStore = useSupplyStore();
    const serviceStore = useServiceStore();
    const userStore = useUserStore();
    const { show: showToast } = useToast();

    const listTab = ref('active');
    const nameFilter = ref('');
    const lowStockOnly = ref(false);
    const showModal = ref(false);
    const showMovement = ref(false);
    const showHistory = ref(false);
    const historySupply = ref(null);
    const showDeleteConfirm = ref(false);
    const saveLoading = ref(false);
    const deleteLoading = ref(false);
    const deleteCheckLoading = ref(false);
    const restoreLoadingId = ref(null);
    const movementLoading = ref(false);
    const editingId = ref(null);
    const deleteTargetId = ref(null);
    const deleteAssessment = ref(null);
    const movementSupply = ref(null);

    const formName = ref('');
    const formTracking = ref('container');
    const formUnit = ref('bandeja');
    const formUnitPlural = ref('bandejas');
    const formCategory = ref('');
    const formReorderLevel = ref(0);
    const formInitialQty = ref(0);
    const formInitialSealed = ref(0);
    const formInitialOpen = ref(0);
    const nameError = ref('');
    const trackingError = ref('');
    const unitError = ref('');
    const initialStockError = ref('');

    const movementType = ref('restock');
    const movementDirection = ref('increase');
    const movementAmount = ref(1);
    const movementNote = ref('');
    const movementNoteError = ref('');
    const movementTypeError = ref('');
    const movementAmountError = ref('');
    const movementActionError = ref('');

    const movementDirectionOptions = [
      { value: 'increase', label: 'Aumentar' },
      { value: 'decrease', label: 'Reduzir' },
    ];

    const trackingOptions = TRACKING_OPTIONS;

    const movementTypeOptions = computed(() => {
      const options = [
        { value: 'restock', label: 'Entrada (reposição)' },
        { value: 'manual_adjust', label: 'Ajuste manual' },
      ];
      if (movementSupply.value?.tracking === 'container') {
        options.push({ value: 'manual_open', label: 'Abrir fechado(s)' });
        options.push({ value: 'manual_deplete', label: 'Esgotar aberto(s)' });
      }
      return options;
    });

    const initialSealedLabel = computed(() => {
      const unit = formUnitPlural.value?.trim() || formUnit.value?.trim() || 'unidades';
      return `Fechados iniciais (${unit})`;
    });

    const initialOpenLabel = computed(() => {
      const unit = formUnitPlural.value?.trim() || formUnit.value?.trim() || 'unidades';
      return `Abertos iniciais (${unit})`;
    });

    const movementAmountLabel = computed(() => {
      if (movementType.value === 'manual_open' || movementType.value === 'manual_deplete') {
        const unit = movementSupply.value?.unitPlural || movementSupply.value?.unit || 'unidades';
        return `Quantidade (${unit})`;
      }
      return 'Quantidade';
    });

    const movementNoteLabel = computed(() => {
      return movementType.value === 'manual_adjust' ? 'Motivo do ajuste' : 'Observação (opcional)';
    });

    const movementNotePlaceholder = computed(() => {
      if (movementType.value === 'manual_adjust') {
        return 'Ex: contagem física, perda, correção';
      }
      if (movementType.value === 'manual_open') {
        return 'Ex: abriu na bancada antes do próximo cliente';
      }
      if (movementType.value === 'manual_deplete') {
        return 'Ex: bandeja vazia esquecida no fechamento';
      }
      return 'Opcional';
    });

    const movementHint = computed(() => {
      const supply = movementSupply.value;
      if (!supply) return '';
      if (movementType.value === 'manual_open') {
        const sealed = Number(supply.sealedCount) || 0;
        const open = openCount(supply.id);
        return `${sealedCountLabel(supply, sealed)} · ${openCountLabel(supply, open)}`;
      }
      if (movementType.value === 'manual_deplete') {
        const open = openCount(supply.id);
        if (open === 0) {
          return 'Não há itens abertos para este insumo.';
        }
        return openCountLabel(supply, open);
      }
      if (movementType.value === 'manual_adjust' && movementDirection.value === 'decrease') {
        if (supply.tracking === 'quantity') {
          return `Em estoque: ${formatQuantityStock(supply)}`;
        }
        return sealedCountLabel(supply, supply.sealedCount);
      }
      return '';
    });

    const activeSupplies = computed(() => supplyStore.activeSupplies);
    const archivedSupplies = computed(() => supplyStore.archivedSupplies);
    const displayedSupplies = computed(() => {
      let list = listTab.value === 'archived' ? archivedSupplies.value : activeSupplies.value;
      if (listTab.value === 'active' && lowStockOnly.value) {
        list = list.filter((supply) => isLowStock(supply));
      }
      const q = (nameFilter.value || '').trim().toLowerCase();
      if (q) {
        list = list.filter((supply) => (supply.name || '').toLowerCase().includes(q));
      }
      return list;
    });
    const openCountBySupplyId = computed(() => supplyStore.openCountBySupplyId);

    const deleteMessage = computed(() => {
      const s = deleteTargetId.value ? supplyStore.supplyById(deleteTargetId.value) : null;
      if (!s) return '';
      if (deleteAssessment.value?.action === 'archive') {
        return formatArchiveDeleteMessage(s.name, deleteAssessment.value);
      }
      return formatHardDeleteMessage(s.name);
    });

    const deleteDialogTitle = computed(() =>
      deleteAssessment.value?.action === 'archive' ? 'Arquivar insumo' : 'Excluir insumo'
    );

    const deleteDialogConfirmLabel = computed(() =>
      deleteAssessment.value?.action === 'archive' ? 'Arquivar' : 'Excluir'
    );

    const deleteDialogLoadingLabel = computed(() =>
      deleteAssessment.value?.action === 'archive' ? 'Arquivando...' : 'Excluindo...'
    );

    function openCount(supplyId) {
      return openCountBySupplyId.value[supplyId] || 0;
    }

    function isLowStock(supply) {
      const level = Number(supply.reorderLevel) || 0;
      if (level <= 0) return false;
      if (supply.tracking === 'quantity') {
        return (Number(supply.quantityOnHand) || 0) <= level;
      }
      return (Number(supply.sealedCount) || 0) <= level;
    }

    function formatQuantityStock(supply) {
      const unit = supply.unitPlural || supply.unit || 'un';
      return `${Number(supply.quantityOnHand) || 0} ${unit}`;
    }

    function resetForm() {
      formName.value = '';
      formTracking.value = 'container';
      formUnit.value = 'bandeja';
      formUnitPlural.value = 'bandejas';
      formCategory.value = '';
      formReorderLevel.value = 0;
      formInitialQty.value = 0;
      formInitialSealed.value = 0;
      formInitialOpen.value = 0;
      nameError.value = '';
      trackingError.value = '';
      unitError.value = '';
      initialStockError.value = '';
    }

    function validateSupplyForm() {
      nameError.value = '';
      trackingError.value = '';
      unitError.value = '';
      initialStockError.value = '';
      let valid = true;
      if (!formName.value.trim()) {
        nameError.value = 'Informe o nome.';
        valid = false;
      }
      if (!formTracking.value || !['quantity', 'container'].includes(formTracking.value)) {
        trackingError.value = 'Selecione o tipo de controle.';
        valid = false;
      }
      if (!formUnit.value?.trim()) {
        unitError.value = 'Informe a unidade (singular).';
        valid = false;
      }
      if (!editingId.value && formTracking.value === 'container') {
        const sealedRaw = Number(formInitialSealed.value);
        const openRaw = Number(formInitialOpen.value);
        if (!Number.isFinite(sealedRaw) || !Number.isFinite(openRaw) || sealedRaw < 0 || openRaw < 0) {
          initialStockError.value = 'Informe quantidades válidas (0 ou mais).';
          valid = false;
        }
      }
      return valid;
    }

    watch(formTracking, () => { trackingError.value = ''; });
    watch(formUnit, () => { unitError.value = ''; });
    watch(movementType, () => {
      movementTypeError.value = '';
      movementActionError.value = '';
      movementAmountError.value = '';
      if (movementType.value === 'restock') {
        movementDirection.value = 'increase';
      }
    });
    watch(movementDirection, () => {
      movementAmountError.value = '';
      movementActionError.value = '';
    });

    function openAdd() {
      editingId.value = null;
      resetForm();
      showModal.value = true;
    }

    function openEdit(supply) {
      editingId.value = supply.id;
      formName.value = supply.name || '';
      formTracking.value = supply.tracking || 'quantity';
      formUnit.value = supply.unit || '';
      formUnitPlural.value = supply.unitPlural || '';
      formCategory.value = supply.category || '';
      formReorderLevel.value = Number(supply.reorderLevel) || 0;
      showModal.value = true;
    }

    async function save() {
      if (!validateSupplyForm()) return;
      saveLoading.value = true;
      try {
        const docData = {
          name: formName.value.trim(),
          tracking: formTracking.value,
          unit: (formUnit.value || 'un').trim(),
          unitPlural: (formUnitPlural.value || formUnit.value || 'un').trim(),
          category: (formCategory.value || '').trim() || null,
          reorderLevel: Number(formReorderLevel.value) || 0,
        };
        if (editingId.value) {
          await setDoc(doc(firestore, 'supplies', editingId.value), docData, { merge: true });
        } else {
          const userId = userStore.user?.uid || null;
          if (formTracking.value === 'quantity') {
            docData.quantityOnHand = Math.max(0, Number(formInitialQty.value) || 0);
            const ref = await addDoc(collection(firestore, 'supplies'), docData);
            if (docData.quantityOnHand > 0) {
              await addDoc(collection(firestore, 'inventoryTransactions'), {
                type: 'restock',
                supplyId: ref.id,
                quantityDelta: docData.quantityOnHand,
                note: 'Estoque inicial',
                createdAt: serverTimestamp(),
              });
            }
          } else {
            const initialSealed = Math.max(0, Math.floor(Number(formInitialSealed.value) || 0));
            const initialOpen = Math.max(0, Math.floor(Number(formInitialOpen.value) || 0));
            const totalReceived = initialSealed + initialOpen;
            docData.sealedCount = initialSealed;

            const supplyRef = doc(collection(firestore, 'supplies'));
            const batch = writeBatch(firestore);
            batch.set(supplyRef, docData);

            if (totalReceived > 0) {
              batch.set(doc(collection(firestore, 'inventoryTransactions')), {
                type: 'restock',
                supplyId: supplyRef.id,
                sealedDelta: totalReceived,
                note: 'Estoque inicial',
                createdAt: serverTimestamp(),
              });
            }

            for (let i = 0; i < initialOpen; i++) {
              const containerRef = doc(collection(firestore, 'openContainers'));
              batch.set(containerRef, {
                supplyId: supplyRef.id,
                status: 'active',
                openedAt: serverTimestamp(),
                openedBy: userId,
                note: 'Aberto no cadastro inicial',
              });
              batch.set(doc(collection(firestore, 'inventoryTransactions')), {
                type: 'open',
                supplyId: supplyRef.id,
                sealedDelta: -1,
                openContainerId: containerRef.id,
                note: 'Aberto no cadastro inicial',
                createdAt: serverTimestamp(),
              });
            }

            await batch.commit();
          }
        }
        showModal.value = false;
        showToast(editingId.value ? 'Insumo alterado.' : 'Insumo salvo.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar.', 'error');
      } finally {
        saveLoading.value = false;
      }
    }

    function openMovement(supply) {
      if (supply.archived) return;
      movementSupply.value = supply;
      movementType.value = 'restock';
      movementDirection.value = 'increase';
      movementAmount.value = 1;
      movementNote.value = '';
      movementNoteError.value = '';
      movementTypeError.value = '';
      movementAmountError.value = '';
      movementActionError.value = '';
      showMovement.value = true;
    }

    function openSupplyHistory(supply) {
      historySupply.value = supply;
      showHistory.value = true;
    }

    function movementSuccessMessage(type, amount, supply) {
      const unit = supply?.unitPlural || supply?.unit || 'unidades';
      if (type === 'manual_open') {
        return `${amount} ${amount === 1 ? (supply?.unit || 'unidade') : unit} aberto(a)(s).`;
      }
      if (type === 'manual_deplete') {
        return `${amount} ${amount === 1 ? (supply?.unit || 'unidade') : unit} esgotado(a)(s).`;
      }
      if (type === 'manual_adjust' && movementDirection.value === 'decrease') {
        return 'Ajuste de redução registrado.';
      }
      return 'Movimentação registrada.';
    }

    async function saveMovement() {
      movementNoteError.value = '';
      movementTypeError.value = '';
      movementAmountError.value = '';
      movementActionError.value = '';
      const supply = movementSupply.value;
      if (!supply) return;
      if (!movementType.value) {
        movementTypeError.value = 'Selecione o tipo de movimentação.';
        return;
      }
      const amount = Math.max(1, Math.floor(Number(movementAmount.value) || 0));
      if (!Number.isFinite(amount) || amount < 1) {
        movementAmountError.value = 'Informe uma quantidade válida (mínimo 1).';
        return;
      }
      if (movementType.value === 'manual_adjust' && !movementNote.value.trim()) {
        movementNoteError.value = 'Informe o motivo do ajuste.';
        return;
      }
      if (movementType.value === 'manual_deplete') {
        const availableOpen = openCount(supply.id);
        if (availableOpen === 0) {
          movementActionError.value = `Não há ${supply.unitPlural || supply.unit || 'itens'} abertos. Use "${openActionLabel(supply)}" se acabou de abrir um na bancada.`;
          return;
        }
        if (availableOpen < amount) {
          movementActionError.value = `Só há ${availableOpen} aberto(a)(s). Reduza a quantidade ou esgote em etapas.`;
          return;
        }
      }
      if (movementType.value === 'manual_open') {
        const sealed = Number(supply.sealedCount) || 0;
        if (sealed === 0) {
          movementActionError.value = `Não há ${supply.unitPlural || supply.unit || 'itens'} fechados. Faça uma entrada (reposição) primeiro.`;
          return;
        }
        if (sealed < amount) {
          movementActionError.value = `Só há ${sealed} fechado(a)(s). Reduza a quantidade ou repor estoque.`;
          return;
        }
      }
      movementLoading.value = true;
      try {
        const supplyRef = doc(firestore, 'supplies', supply.id);
        const userId = userStore.user?.uid || null;

        if (movementType.value === 'restock') {
          if (supply.tracking === 'quantity') {
            const onHand = Number(supply.quantityOnHand) || 0;
            await updateDoc(supplyRef, { quantityOnHand: onHand + amount });
            await addDoc(collection(firestore, 'inventoryTransactions'), {
              type: 'restock',
              supplyId: supply.id,
              quantityDelta: amount,
              note: movementNote.value.trim() || null,
              createdAt: serverTimestamp(),
            });
          } else {
            const sealed = Number(supply.sealedCount) || 0;
            await updateDoc(supplyRef, { sealedCount: sealed + amount });
            await addDoc(collection(firestore, 'inventoryTransactions'), {
              type: 'restock',
              supplyId: supply.id,
              sealedDelta: amount,
              note: movementNote.value.trim() || null,
              createdAt: serverTimestamp(),
            });
          }
        } else if (movementType.value === 'manual_adjust') {
          const delta = movementDirection.value === 'decrease' ? -amount : amount;
          if (supply.tracking === 'quantity') {
            const onHand = Number(supply.quantityOnHand) || 0;
            const next = onHand + delta;
            if (next < 0) {
              movementAmountError.value = `Não é possível reduzir mais que o estoque atual (${onHand}).`;
              return;
            }
            await updateDoc(supplyRef, { quantityOnHand: next });
            await addDoc(collection(firestore, 'inventoryTransactions'), {
              type: 'manual_adjust',
              supplyId: supply.id,
              quantityDelta: delta,
              note: movementNote.value.trim(),
              createdAt: serverTimestamp(),
            });
          } else {
            const sealed = Number(supply.sealedCount) || 0;
            const next = sealed + delta;
            if (next < 0) {
              movementAmountError.value = `Não é possível reduzir mais que o estoque fechado atual (${sealed}).`;
              return;
            }
            await updateDoc(supplyRef, { sealedCount: next });
            await addDoc(collection(firestore, 'inventoryTransactions'), {
              type: 'manual_adjust',
              supplyId: supply.id,
              sealedDelta: delta,
              note: movementNote.value.trim(),
              createdAt: serverTimestamp(),
            });
          }
        } else if (movementType.value === 'manual_open') {
          const sealed = Number(supply.sealedCount) || 0;
          await updateDoc(supplyRef, { sealedCount: sealed - amount });
          const batch = writeBatch(firestore);
          for (let i = 0; i < amount; i++) {
            const containerRef = doc(collection(firestore, 'openContainers'));
            batch.set(containerRef, {
              supplyId: supply.id,
              status: 'active',
              openedAt: serverTimestamp(),
              openedBy: userId,
              note: movementNote.value.trim() || null,
            });
            const txRef = doc(collection(firestore, 'inventoryTransactions'));
            batch.set(txRef, {
              type: 'open',
              supplyId: supply.id,
              sealedDelta: -1,
              openContainerId: containerRef.id,
              note: movementNote.value.trim() || 'Abertura manual no estoque',
              createdAt: serverTimestamp(),
            });
          }
          await batch.commit();
        } else if (movementType.value === 'manual_deplete') {
          const openQuery = query(
            collection(firestore, 'openContainers'),
            where('supplyId', '==', supply.id),
            where('status', '==', 'active'),
            orderBy('openedAt', 'asc'),
            limit(amount),
          );
          const openSnap = await getDocs(openQuery);
          if (openSnap.size < amount) {
            movementActionError.value = openSnap.size === 0
              ? `Não há ${supply.unitPlural || supply.unit || 'itens'} abertos para esgotar.`
              : `Só há ${openSnap.size} aberto(a)(s). Reduza a quantidade.`;
            showToast(movementActionError.value, 'error');
            return;
          }
          const batch = writeBatch(firestore);
          openSnap.docs.forEach((containerDoc) => {
            batch.update(containerDoc.ref, {
              status: 'depleted',
              depletedAt: serverTimestamp(),
              depletedBy: userId,
              note: movementNote.value.trim() || 'Ajuste manual no estoque',
            });
            const txRef = doc(collection(firestore, 'inventoryTransactions'));
            batch.set(txRef, {
              type: 'deplete',
              supplyId: supply.id,
              openContainerId: containerDoc.id,
              note: movementNote.value.trim() || 'Ajuste manual no estoque',
              createdAt: serverTimestamp(),
            });
          });
          await batch.commit();
        }
        showMovement.value = false;
        showToast(movementSuccessMessage(movementType.value, amount, supply), 'success');
      } catch (err) {
        console.error('saveMovement failed', err);
        movementActionError.value = 'Não foi possível registrar. Verifique permissões e tente novamente.';
        showToast('Não foi possível registrar.', 'error');
      } finally {
        movementLoading.value = false;
      }
    }

    async function handleDeleteClick(supply) {
      deleteTargetId.value = supply.id;
      deleteAssessment.value = null;
      deleteCheckLoading.value = true;
      try {
        const assessment = await assessSupplyDeletion(
          supply.id,
          serviceStore.services,
          openCount(supply.id),
        );
        if (assessment.action === 'block') {
          showToast(formatBlockOpenContainersMessage(supply, assessment.activeOpenCount), 'error');
          deleteTargetId.value = null;
          return;
        }
        deleteAssessment.value = assessment;
        showDeleteConfirm.value = true;
      } catch (err) {
        console.error('handleDeleteClick failed', err);
        showToast('Não foi possível verificar o insumo.', 'error');
        deleteTargetId.value = null;
      } finally {
        deleteCheckLoading.value = false;
      }
    }

    async function doDelete() {
      if (!deleteTargetId.value) return;
      const assessment = deleteAssessment.value;
      deleteLoading.value = true;
      try {
        const supplyRef = doc(firestore, 'supplies', deleteTargetId.value);
        if (assessment?.action === 'archive') {
          const batch = writeBatch(firestore);
          batch.update(supplyRef, {
            archived: true,
            archivedAt: serverTimestamp(),
            archivedBy: userStore.user?.uid || null,
          });
          const serviceUpdates = buildServiceUpdatesUnlinkingSupply(
            deleteTargetId.value,
            serviceStore.services,
          );
          for (const { serviceId, supplyConfig } of serviceUpdates) {
            batch.update(doc(firestore, 'services', serviceId), { supplyConfig });
          }
          await batch.commit();
          const unlinkCount = serviceUpdates.length;
          showToast(
            unlinkCount > 0
              ? `Insumo arquivado e removido de ${unlinkCount} serviço(s).`
              : 'Insumo arquivado.',
            'success',
          );
        } else {
          await deleteDoc(supplyRef);
          showToast('Insumo excluído.', 'success');
        }
        deleteTargetId.value = null;
        deleteAssessment.value = null;
        showDeleteConfirm.value = false;
      } catch (err) {
        console.error('doDelete failed', err);
        showToast('Não foi possível concluir.', 'error');
      } finally {
        deleteLoading.value = false;
      }
    }

    async function restoreSupply(supply) {
      restoreLoadingId.value = supply.id;
      try {
        await updateDoc(doc(firestore, 'supplies', supply.id), {
          archived: false,
          archivedAt: deleteField(),
          archivedBy: deleteField(),
        });
        showToast('Insumo reativado.', 'success');
        listTab.value = 'active';
      } catch (err) {
        console.error('restoreSupply failed', err);
        showToast('Não foi possível reativar.', 'error');
      } finally {
        restoreLoadingId.value = null;
      }
    }

    return {
      listTab,
      nameFilter,
      lowStockOnly,
      activeSupplies,
      archivedSupplies,
      displayedSupplies,
      showModal,
      showMovement,
      showHistory,
      historySupply,
      showDeleteConfirm,
      saveLoading,
      deleteLoading,
      deleteCheckLoading,
      restoreLoadingId,
      deleteTargetId,
      movementLoading,
      editingId,
      formName,
      formTracking,
      formUnit,
      formUnitPlural,
      formCategory,
      formReorderLevel,
      formInitialQty,
      formInitialSealed,
      formInitialOpen,
      initialSealedLabel,
      initialOpenLabel,
      initialStockError,
      nameError,
      trackingError,
      unitError,
      movementSupply,
      movementType,
      movementDirection,
      movementDirectionOptions,
      movementAmount,
      movementAmountLabel,
      movementAmountError,
      movementActionError,
      movementHint,
      movementNoteLabel,
      movementNotePlaceholder,
      movementNote,
      movementNoteError,
      movementTypeError,
      trackingOptions,
      movementTypeOptions,
      deleteMessage,
      deleteDialogTitle,
      deleteDialogConfirmLabel,
      deleteDialogLoadingLabel,
      openAdd,
      openEdit,
      save,
      openMovement,
      openSupplyHistory,
      saveMovement,
      handleDeleteClick,
      doDelete,
      restoreSupply,
      isLowStock,
      formatQuantityStock,
      openCount,
      openCountLabel,
      sealedCountLabel,
    };
  },
};
</script>

<style scoped>
.inventory-tabs {
  display: flex;
  gap: var(--space-xs);
  margin-bottom: var(--space-md);
}

.inventory-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.inventory-tab--active {
  background: var(--color-accent-soft);
  border-color: color-mix(in srgb, var(--color-accent) 35%, transparent);
  color: var(--color-accent);
}

.inventory-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 0.7rem;
  background: color-mix(in srgb, var(--color-accent) 18%, white);
  color: var(--color-accent);
}

.inventory-filters {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
}

.inventory-search-wrap {
  position: relative;
}

.inventory-search-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.inventory-search-input {
  width: 100%;
  padding: 10px 12px 10px 44px;
  font-size: 1rem;
  font-family: inherit;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.inventory-search-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.inventory-search-input::placeholder {
  color: var(--color-text-muted);
}

.inventory-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 22px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.inventory-filter-checkbox {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  user-select: none;
}

.card--archived {
  opacity: 0.88;
  background: color-mix(in srgb, var(--color-text-muted) 6%, var(--color-surface));
}

.archived-badge {
  display: inline-flex;
  margin: var(--space-sm) 0 0;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  background: color-mix(in srgb, var(--color-text-muted) 12%, white);
  border: 1px solid var(--color-border);
}

.inventory-card-icon {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.card--low-stock {
  border: 2px solid var(--color-warning);
  background: var(--color-warning-bg);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--color-warning) 22%, transparent),
    0 3px 12px color-mix(in srgb, var(--color-warning) 16%, transparent);
}

.card--low-stock:hover {
  border-color: var(--color-warning);
  box-shadow:
    0 0 0 2px color-mix(in srgb, var(--color-warning) 26%, transparent),
    0 4px 14px color-mix(in srgb, var(--color-warning) 20%, transparent);
}

.card--low-stock .inventory-card-icon {
  background: color-mix(in srgb, var(--color-warning) 28%, white);
  color: #9a6b00;
}

.low-stock-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: var(--space-sm) 0 0;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #8a5f00;
  background: color-mix(in srgb, var(--color-warning) 30%, white);
  border: 1px solid color-mix(in srgb, var(--color-warning) 50%, transparent);
}

.low-stock-badge .material-icons {
  font-size: 1rem;
}

.empty-hint {
  text-align: center;
  color: var(--color-text-muted);
  margin: var(--space-xl) 0;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
}

.field-invalid :deep(.v-select .vs__dropdown-toggle) {
  border-color: var(--color-error);
}

.modal-footer--inline {
  display: flex;
  gap: var(--space-sm);
  justify-content: flex-end;
  margin-top: var(--space-lg);
  padding-top: var(--space-md);
  border-top: 1px solid var(--color-border);
}

.field-hint {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin: var(--space-xs) 0 0;
}

.movement-action-error {
  margin-top: var(--space-md);
  padding: var(--space-sm);
  background: color-mix(in srgb, var(--color-error) 8%, transparent);
  border-radius: var(--radius-sm);
}
</style>
